import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Brain, TrendingUp, TrendingDown, Minus, Activity, Users, Newspaper, AlertTriangle, Target, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

import { API } from "../config/api";

// Moved outside component to avoid re-creation on each render
const DataSummaryCard = ({ data }) => {
  if (!data) return null;
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
      <div className="bg-blue-50 p-2 rounded border border-blue-200">
        <p className="text-[10px] text-blue-600 font-medium">RSI</p>
        <p className={`text-sm font-bold ${data.technicals?.rsi > 70 ? 'text-red-600' : data.technicals?.rsi < 30 ? 'text-green-600' : 'text-slate-700'}`}>
          {data.technicals?.rsi?.toFixed(1) || 'N/A'}
        </p>
      </div>
      <div className="bg-purple-50 p-2 rounded border border-purple-200">
        <p className="text-[10px] text-purple-600 font-medium">MFI</p>
        <p className={`text-sm font-bold ${data.technicals?.mfi > 80 ? 'text-red-600' : data.technicals?.mfi < 20 ? 'text-green-600' : 'text-slate-700'}`}>
          {data.technicals?.mfi?.toFixed(1) || 'N/A'}
        </p>
      </div>
      <div className="bg-indigo-50 p-2 rounded border border-indigo-200">
        <p className="text-[10px] text-indigo-600 font-medium">Basket Breadth</p>
        <p className={`text-sm font-bold ${data.basket_breadth >= 60 ? 'text-green-600' : data.basket_breadth <= 40 ? 'text-red-600' : 'text-yellow-600'}`}>
          {data.basket_breadth?.toFixed(0) || 0}%
        </p>
      </div>
      <div className="bg-slate-50 p-2 rounded border border-slate-200">
        <p className="text-[10px] text-slate-600 font-medium">Trend</p>
        <p className="text-sm font-bold text-slate-700">
          {data.market_state?.trend || 'N/A'}
        </p>
      </div>
    </div>
  );
};

const SentimentFast = ({ ticker, basketHealthLoaded = true }) => {
  const [sentiment, setSentiment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetchedTicker, setLastFetchedTicker] = useState(null);

  // Clear data when ticker changes
  useEffect(() => {
    if (ticker !== lastFetchedTicker) {
      setSentiment(null);
    }
  }, [ticker, lastFetchedTicker]);

  const fetchSentiment = useCallback(async (tickerToFetch) => {
    if (!tickerToFetch) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/sentiment-fast/${tickerToFetch}`);
      setSentiment(response.data);
      setLastFetchedTicker(tickerToFetch);
    } catch (error) {
      console.error("Error fetching sentiment:", error);
      console.warn("Failed to load sentiment analysis - retrying silently in background");
    } finally {
      setLoading(false);
    }
  }, []);

  // Only fetch sentiment AFTER basket health is loaded
  useEffect(() => {
    if (ticker && basketHealthLoaded) {
      fetchSentiment(ticker);
    }
  }, [ticker, basketHealthLoaded, fetchSentiment]);

  // Auto-refresh every 3 minutes (180000ms)
  useEffect(() => {
    if (!ticker || !basketHealthLoaded) return;
    
    const refreshInterval = setInterval(() => {
      console.log('Auto-refreshing AI Sentiment Analysis...');
      fetchSentiment(ticker);
    }, 180000); // 3 minutes
    
    return () => clearInterval(refreshInterval);
  }, [ticker, basketHealthLoaded, fetchSentiment]);

  const getSentimentBadge = (text) => {
    if (!text) return { label: 'Neutral', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Minus };
    const lower = text.toLowerCase();
    if (lower.includes('bullish')) return { label: 'Bullish', color: 'bg-green-100 text-green-700 border-green-300', icon: TrendingUp };
    if (lower.includes('bearish')) return { label: 'Bearish', color: 'bg-red-100 text-red-700 border-red-300', icon: TrendingDown };
    return { label: 'Neutral', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Minus };
  };

  const parseComprehensiveAnalysis = (analysis) => {
    if (!analysis) return { 
      overallSentiment: 'Unknown', 
      confidence: 0,
      technicalOutlook: [], 
      basketContext: [], 
      newsImpact: [], 
      risks: [],
      actionableInsight: ''
    };
    
    const sections = {
      overallSentiment: 'Unknown',
      confidence: 0,
      technicalOutlook: [],
      basketContext: [],
      newsImpact: [],
      risks: [],
      actionableInsight: ''
    };
    
    // Extract overall sentiment and confidence
    const sentimentMatch = analysis.match(/\*\*OVERALL SENTIMENT\*\*:?\s*\[?(BULLISH|BEARISH|NEUTRAL)\]?/i);
    if (sentimentMatch) {
      sections.overallSentiment = sentimentMatch[1];
    } else if (analysis.toLowerCase().includes('bullish')) {
      sections.overallSentiment = 'Bullish';
    } else if (analysis.toLowerCase().includes('bearish')) {
      sections.overallSentiment = 'Bearish';
    }
    
    const confidenceMatch = analysis.match(/confidence[:\s]*(\d+)/i);
    if (confidenceMatch) {
      sections.confidence = parseInt(confidenceMatch[1]);
    }
    
    // Parse sections
    const extractBulletPoints = (text, sectionName) => {
      const sectionRegex = new RegExp(`\\*\\*${sectionName}\\*\\*[:\\s]*([\\s\\S]*?)(?=\\*\\*|$)`, 'i');
      const match = text.match(sectionRegex);
      if (match) {
        const points = match[1].split('\n')
          .filter(line => line.trim().match(/^[-•*]|^\d+\./))
          .map(line => line.replace(/^[-•*\d.]+\s*/, '').trim())
          .filter(line => line.length > 0);
        return points.slice(0, 3);
      }
      return [];
    };
    
    sections.technicalOutlook = extractBulletPoints(analysis, 'TECHNICAL OUTLOOK');
    sections.basketContext = extractBulletPoints(analysis, 'BASKET/SECTOR CONTEXT');
    sections.newsImpact = extractBulletPoints(analysis, 'NEWS IMPACT');
    sections.risks = extractBulletPoints(analysis, 'KEY RISKS');
    
    // Extract actionable insight
    const insightMatch = analysis.match(/\*\*ACTIONABLE INSIGHT\*\*:?\s*([^\n]+)/i);
    if (insightMatch) {
      sections.actionableInsight = insightMatch[1].replace(/^[-•*]\s*/, '').trim();
    }
    
    return sections;
  };

  return (
    <Card className="border-slate-200 bg-white/80 backdrop-blur-sm">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-600" />
            AI Sentiment Analysis
            {sentiment?.cached && (
              <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-2 border border-blue-200">
                Cached ({sentiment.cache_age_minutes}m)
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-600 border-purple-200">
              <RefreshCw className="w-2.5 h-2.5 mr-1" />
              Auto-refresh 3m
            </Badge>
            {sentiment && !loading && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchSentiment(ticker)}
                className="h-7 px-2"
              >
                <RefreshCw className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-600">
          Comprehensive analysis using technicals, basket health, and {sentiment?.newsCount || 0} news sources
        </p>
      </CardHeader>
      <CardContent>
        {!basketHealthLoaded ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            <span className="ml-2 text-sm text-slate-600">Waiting for basket/sector health data...</span>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            <span className="ml-2 text-sm text-slate-600">Analyzing all data points...</span>
          </div>
        ) : sentiment ? (
          <div className="space-y-4">
            {/* Data Summary */}
            <DataSummaryCard data={sentiment.data_summary} />
            
            {/* Comprehensive Analysis */}
            {(() => {
              const parsed = parseComprehensiveAnalysis(sentiment.analysis);
              const badgeInfo = getSentimentBadge(parsed.overallSentiment);
              const Icon = badgeInfo.icon;
              
              return (
                <>
                  {/* Overall Sentiment */}
                  <div className={`flex items-center gap-3 p-3 rounded-lg border-2 ${badgeInfo.color}`}>
                    <Icon className="w-6 h-6" />
                    <div className="flex-1">
                      <p className="text-xs opacity-70 mb-1">Overall Sentiment</p>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold">{parsed.overallSentiment}</span>
                        {parsed.confidence > 0 && (
                          <Badge variant="outline" className="text-xs">
                            Confidence: {parsed.confidence}/10
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Analysis Sections Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Technical Outlook */}
                    {parsed.technicalOutlook.length > 0 && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <h4 className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1">
                          <Activity className="w-3 h-3" />
                          Technical Outlook
                        </h4>
                        <ul className="space-y-1">
                          {parsed.technicalOutlook.map((point, idx) => (
                            <li key={idx} className="text-xs text-blue-700 flex items-start gap-1">
                              <span className="text-blue-500 mt-0.5">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Basket/Sector Context */}
                    {parsed.basketContext.length > 0 && (
                      <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-bold text-purple-800 flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {sentiment.data_summary?.is_etf ? 'ETF Basket' : 'Sector'} Context
                          </h4>
                          {sentiment.data_summary?.basket_breadth !== undefined && (
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                              sentiment.data_summary.basket_breadth >= 60 ? 'bg-green-200 text-green-800' :
                              sentiment.data_summary.basket_breadth <= 40 ? 'bg-red-200 text-red-800' :
                              'bg-yellow-200 text-yellow-800'
                            }`}>
                              Breadth: {sentiment.data_summary.basket_breadth?.toFixed?.(0) || "0"}% ({sentiment.data_summary.basket_status})
                            </span>
                          )}
                        </div>
                        <ul className="space-y-1">
                          {parsed.basketContext.map((point, idx) => (
                            <li key={idx} className="text-xs text-purple-700 flex items-start gap-1">
                              <span className="text-purple-500 mt-0.5">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* News Impact */}
                    {parsed.newsImpact.length > 0 && (
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                        <h4 className="text-xs font-bold text-green-800 mb-2 flex items-center gap-1">
                          <Newspaper className="w-3 h-3" />
                          News Impact
                        </h4>
                        <ul className="space-y-1">
                          {parsed.newsImpact.map((point, idx) => (
                            <li key={idx} className="text-xs text-green-700 flex items-start gap-1">
                              <span className="text-green-500 mt-0.5">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Key Risks */}
                    {parsed.risks.length > 0 && (
                      <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                        <h4 className="text-xs font-bold text-red-800 mb-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Key Risks
                        </h4>
                        <ul className="space-y-1">
                          {parsed.risks.map((risk, idx) => (
                            <li key={idx} className="text-xs text-red-700 flex items-start gap-1">
                              <span className="text-red-500 mt-0.5">•</span>
                              <span>{risk}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Actionable Insight */}
                  {parsed.actionableInsight && (
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 rounded-lg border-2 border-indigo-200">
                      <h4 className="text-xs font-bold text-indigo-800 mb-1 flex items-center gap-1">
                        <Target className="w-3 h-3" />
                        Actionable Insight
                      </h4>
                      <p className="text-sm text-indigo-900 font-medium">{parsed.actionableInsight}</p>
                    </div>
                  )}

                  {/* News Headlines with Links */}
                  {sentiment.news_articles && sentiment.news_articles.length > 0 && (
                    <div className="mt-3 bg-slate-50 p-3 rounded-lg border border-slate-200">
                      <h4 className="text-xs font-bold text-slate-800 mb-2 flex items-center gap-1">
                        <Newspaper className="w-3 h-3" />
                        📰 {sentiment.news_articles.length} News Headlines
                      </h4>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {sentiment.news_articles.map((article, idx) => (
                          <div key={idx} className="text-xs p-2 bg-white rounded border border-slate-100 hover:border-blue-300 transition-colors">
                            {article.link ? (
                              <a 
                                href={article.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium inline-flex items-center gap-1"
                              >
                                {article.title || 'Untitled'}
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              </a>
                            ) : (
                              <span className="text-slate-700">{article.title || 'Untitled'}</span>
                            )}
                            {article.source && (
                              <span className="text-slate-400 ml-2 text-[10px]">— {article.source}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Full Analysis (shown by default) */}
                  <div className="mt-3 bg-gradient-to-r from-slate-50 to-blue-50 p-3 rounded-lg border border-slate-200">
                    <h4 className="text-xs font-bold text-slate-800 mb-2 flex items-center gap-1">
                      <Brain className="w-3 h-3" />
                      🤖 Raw AI Analysis
                    </h4>
                    <div className="p-3 bg-white rounded text-xs text-slate-700 whitespace-pre-wrap max-h-80 overflow-y-auto border border-slate-100 leading-relaxed">
                      {sentiment.analysis}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        ) : (
          <p className="text-center text-slate-600 py-4 text-sm">No sentiment data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default SentimentFast;
